/**
 * Add to theme.liquid
 * <script async type="module" src="{{ 'jod-global-qwik-loader.js' | asset_url }}"></script>
 *
 * // example usage
 * <div jod-qwik="hover->{{ 'jod-qwik-kadena-slider.js' | asset_url }}#handleHover"></div>
 *
 * // example usage with intersection observer
 * <div jod-qwik="intersection->{{ 'jod-qwik-kadena-slider.js' | asset_url }}#handleHover" data-root-margin="0px 0px 300px 0px" data-intersection-percentage="0.5"></div>
 *
 * // example usage with desktop and mobile
 * <div jod-qwik="hoverDesktop->{{ 'jod-qwik-kadena-slider.js' | asset_url }}#handleHover"></div>
 * <div jod-qwik="hoverMobile->{{ 'jod-qwik-kadena-slider.js' | asset_url }}#handleHover"></div>
 *
 * // jod-qwik-kadena-slider.js
 * ```
 * export default {
 *  handleHover: function () {
 *   console.log('jodQwikLoader');
 *  }
 * }
 * ```
 */

var callCount = 0;
var isQwikLoading = false;
window.addEventListener('load', onLoadPage);
window.addEventListener('DOMContentLoaded', updateJODQwikLoads);

// when the dom is mutated look for new jod-qwik elements
function onLoadPage() {
  const observer = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      if (mutation.type == 'childList') {
        const requiresUpdate = !!document.querySelector('[jod-qwik]:not([jod-qwik-loaded])');
        if (requiresUpdate) {
          updateJODQwikLoads();
        }
      }
    });
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

export function updateJODQwikLoads() {
  if (isQwikLoading) return;
  isQwikLoading = true;
  setTimeout(() => {
    isQwikLoading = false;
  }, 5000);
  console.log('jod-qwik', callCount++);
  document.querySelectorAll('[jod-qwik]').forEach(function (el) {
    const jodQwikEvent = el.getAttribute('jod-qwik')?.split('->').shift()?.split('@').shift();

    if (!jodQwikEvent || el.hasAttribute('jod-qwik-loaded')) return;
    if (!el.hasAttribute('jod-qwik-loaded')) el.setAttribute('jod-qwik-loaded', 'true');

    // map colloquial event names to their proper js event names
    const eventMaps = (commonEventName) => {
      if (commonEventName.includes('mobile')) {
        return window.innerWidth < 768 ? eventMaps(commonEventName.replace('mobile', '').toLowerCase()) : undefined;
      } else if (commonEventName.includes('desktop')) {
        return window.innerWidth > 768 ? eventMaps(commonEventName.replace('desktop', '').toLowerCase()) : undefined;
      }

      switch (true) {
        case commonEventName.includes('hover'):
          return 'mouseover';
      }
      return commonEventName;
    };
    const eventType = eventMaps(jodQwikEvent);
    if (!eventType) return;
    if (eventType == 'intersection') {
      watchQwikIntersection(el);
      return;
    }
    const qwikAttribute = el.getAttribute('jod-qwik');
    const target = qwikAttribute?.includes('@window')
      ? window
      : qwikAttribute?.includes('@document')
        ? document
        : el;
    target.addEventListener(eventType, (e) => { onQwikDataEvent(e, el) }, { once: true });
  });
}

function onQwikDataEvent(event, el) {
  event.preventDefault();
  const jodQwikData = el.getAttribute('jod-qwik').split('->').pop();
  loadDynamicModule(jodQwikData, el);
}

/**
 * Intersection Observer for lazy loading
 * Params:
 *  rootMargin - default is 300px below the fold
 *  intersectionPercentage - default is 0.5 (takes precedence over intersectingHeight)
 *  intersectingHeight - 0px
 *
 * @example
 * <div jod-qwik="intersection->{{ 'jod-qwik-kadena-slider.js' | asset_url }}#handleHover"
 *  data-root-margin="0px 0px 300px 0px"
 *  data-intersection-percentage="0.5"
 *  data-intersection-height="100"></div>
 * @param {HTMLElement} el
 */
function watchQwikIntersection(host) {
  // if (host.getAttribute('jod-qwik-loaded')) return;
  let el = host;
  if (host.tagName == 'TEMPLATE') {
    el = host.closest('.shopify-section');
  }
  // default rootMargin is 300px below the fold
  const rootMargin = el?.dataset.rootMargin || '0px 0px 300px 0px';
  const observer = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      const intersectingPercentage = el?.dataset.intersectionPercentage;
      const intersectingHeight = el?.dataset.intersectionHeight;
      // console.log('intersectingHeight', intersectingHeight, 'entry.intersectionRatio', entry.intersectionRect.height, entry)
      const isIntersecting = intersectingPercentage
        ? entry.intersectionRatio >= Number(intersectingPercentage)
        : intersectingHeight
          ? entry.intersectionRect.height >= Number(intersectingHeight)
          : entry.isIntersecting;
      if (isIntersecting) {
        const jodQwikData = /** @type string */ (host.getAttribute('jod-qwik')?.split('->').pop());
        const renderOnIntersection = jodQwikData == 'render' && host.tagName == 'TEMPLATE';
        if (renderOnIntersection) {
          loadSectionFromTemplate(host);
          observer.unobserve(entry.target);
        }
        else {
          loadDynamicModule(jodQwikData);
          observer.unobserve(entry.target);
        }
      }
    });
  }, { rootMargin, threshold: [0.1, .2, .3, .4, .5, .6, .7, .8, .9, .99] });

  observer.observe(el);
}

function loadDynamicModule(jodQwikData, thisEl = null) {
  const linkSrc = jodQwikData.split('#')[0];
  const method = jodQwikData.split('#')[1];
  // dynamically import the script
  import(linkSrc)
    .then(module => module.default)
    // .then((defaults) => { console.log('jeshua lazy defaults', defaults, method, defaults?.method, defaults[method]); });
    .then(methods => methods[method].bind(thisEl)());
}

/**
 *
 * @param {HTMLElement} template
 */
function loadSectionFromTemplate(template) {
  // console.log('jeshua here', template)
  if (!template.getAttribute('jod-qwik-loaded-template-render')) {
    const content = document.createElement('div');
    content.appendChild(template.content.cloneNode(true));
    template.setAttribute('jod-qwik-loaded-template-render', 'true');
    template.insertAdjacentElement('afterend', content);

    // console.log('jeshua intersection el is intersecting 8')
    // check for any other qwik loads that may now be rendered
    // from template content
    updateJODQwikLoads();
  }
}

/**
 * ********************************************************************************
 *
 *
 * This is code used for creating dynamic html
 *
 *
 * ********************************************************************************
 */
export default class JodStorageElement extends HTMLElement {
  constructor() {
    super();
    this.initialize();
  }
  // children implement
  initialize() {
    // always call this last in children
    // super.initialize();
    this._initialize();
  }

  _initialize() {
    // set the initial data for the app and the store
    this.data = this._useBoundElementsStore('data', this.data || {});
    console.log('initializing', this.tagName);
  }


  connectedCallback() {
    this._setupActionListeners();
    // this._render('all'); // this was just to verify is possible
  }

  _loadHQImages({ target, ...rest }) {
    const hqImage = target?.closest(".lqip-container").querySelector(".hq-img")
    hqImage?.removeAttribute("hidden");
    hqImage?.removeAttribute("loading");
  }

  _setupActionListeners() {
    this.querySelectorAll(`[data-action*='${this.tagName.toLowerCase()}#']`).forEach(el => {
      el.getAttribute('data-action').split(' ').forEach(action => {
        if (el.getAttribute('data-loaded-actions')?.includes(action)) return;
        el.setAttribute('data-loaded-actions', `${el.getAttribute('data-loaded-actions') || ''} ${action}`);
        // Setup Actions
        let eventAndMethod = action.split('->');
        if (eventAndMethod.length !== 2) {
          eventAndMethod.unshift(this._defaultEventFor(el.tagName));
        }
        let [eventName, methodName] = eventAndMethod;
        if (eventName.toLocaleLowerCase().includes('mobile') && window.innerWidth >= 768) {
          return;
        } else if (eventName.toLocaleLowerCase().includes('desktop') && window.innerWidth < 768) {
          return;
        }

        // Setup Action Params
        // All dataset attributes will be passed in as params
        const params = el.dataset;
        // always access the element with the action is on via the element param
        params.element = el;
        methodName = methodName.replace(`${this.tagName.toLowerCase()}#`, 'this.');

        // Add event listener
        // We do allow inner functions to be passed in as a string
        // This is useful for passing in a function that needs to be called with a specific context
        // For example, if you need to call a function on the parent element
        // this is done by wrapping the function in curly braces; e.g. {this.myFunction()}
        console.log(this.tagName, `Registering New {${eventName}} Action: ${methodName}`);
        if (methodName.startsWith('{') && methodName.endsWith('}')) {
          // Inner function
          let innerFunctionString = methodName;
          innerFunctionString = `(() => ${innerFunctionString})()`; // wrap in parens to make it a function call
          const innerFunction = new Function('params', `return ${innerFunctionString}`);
          // adding a @ to the event name will allow you to specify a global element to attach the event listener to (window or document or this or custom element tag name)
          // e.g. data-action="myCustomEvent@window" will attach the myCustomEvent event listener to the window
          // e.g. data-action="myCustomEvent@jod-product-card" will attach the myCustomEvent event listener to the parent jod-product-card element
          // you can then dispatch the event with window.dispatchEvent(new CustomEvent('myCustomEvent'))
          // See https://stimulus.hotwired.dev/reference/actions#global-events
          if (eventName.includes('@')) {
            const eventElName = eventName.split('@')[1];
            eventName = eventName.replace(`@${eventElName}`, '').toLocaleLowerCase();
            const customEventEl = eventElName === 'window'
              ? window
              : eventElName === 'document'
                ? document
                : eventElName === this.tagName.toLowerCase()
                  ? el.closest(eventElName)
                  : document.querySelector(eventElName);
            if (!customEventEl) {
              console.assert(!!customEventEl, `Element for data-action-${methodName} not found for ${eventName} event.`);
              return;
            }
            customEventEl.addEventListener(eventName, event => innerFunction.bind(this, { event, ...params })());
            return;
          }
          el.addEventListener(eventName, event => innerFunction.bind(this, { event, ...params })());
        } else {
          methodName = methodName.replace('this.', '');
          if (eventName == 'intersection') {
            // Intersection Observer
            const observer = new IntersectionObserver((entries, observer) => {
              entries.forEach(entry => {
                if (entry.isIntersecting) {
                  this[methodName].bind(this)({ target: entry.target, ...params });
                  observer.disconnect();
                }
              });
            }, { threshold: [0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9] });
            observer.observe(el);
            return;
          }
          if (!eventName || !methodName || !this[methodName]) {
            console.assert(!!methodName && !!this[methodName], `Function for data-action-${methodName} not implemented for ${eventName} event.`);
            return;
          };
          if (eventName.includes('@')) {
            const eventElName = eventName.split('@')[1];
            eventName = eventName.replace(`@${eventElName}`, '').toLocaleLowerCase();;
            const customEventEl = eventElName === 'window'
              ? window
              : eventElName === 'document'
                ? document
                : eventElName === this.tagName.toLowerCase()
                  ? el.closest(eventElName)
                  : document.querySelector(eventElName);
            if (!customEventEl) {
              console.assert(!!customEventEl, `Element for data-action-${methodName} not found for ${eventName} event.`);
              return;
            }
            customEventEl.addEventListener(eventName, event => this[methodName].bind(this)({ event, ...params }));
            return;
          }
          el.addEventListener(eventName, event => this[methodName].bind(this)({ event, ...params }));
        }
      });
    });
  }

  /**
   * Gets the default event for a given tag name
      | TagName | default event |
      | --- | --- |
      | a | click |
      | button | click |
      | details | toggle |
      | form | submit |
      | input | input |
      | input | type=submit	click |
      | select | change |
      | textarea | input |
  */
  _defaultEventFor(tagName) {
    switch (tagName) {
      case 'A':
      case 'BUTTON':
        return 'mousedown';
      case 'DETAILS':
        return 'toggle';
      case 'FORM':
        return 'submit';
      case 'SELECT':
        return 'change';
      case 'INPUT':
      case 'TEXTAREA':
        return 'input';
      default:
        return 'mousedown';
    }
  }
  _defaultBindTypes(tagName) {
    switch (tagName) {
      case 'SELECT':
      case 'INPUT':
      case 'TEXTAREA':
        return 'value';
      default:
        return 'textContent';
    }
  }

  /**
   * A proxy object that will call _render() whenever a property is set
   */
  _useBoundElementsStore(varName, initialState = {}) {
    const dataWithoutProxy = initialState;
    const self = this;
    const data = new Proxy(dataWithoutProxy, {
      get(obj, key) {
        return obj[key];
      },
      set(obj, key, value) {
        if (obj[key] === value) return true;
        obj[key] = value;
        // self._debounce(() => self._render(`${varName}.${String(key)}`), 100)
        self._render(`${varName}.${String(key)}`)

        return true;
      }
    })
    return data;
  }

  // equivalent to using react context for global state, is fine for small apps
  _render(bindKey = 'data') {
    if (bindKey == 'all') {
      Object.keys(this.data).map(bindKey => {
        this._render(`data.${bindKey}`);
      })
      return;
    }
    this.querySelectorAll(`
      [data-class *= "${this.tagName.toLowerCase()}#${bindKey}"],
      [data-attribute *= "${this.tagName.toLowerCase()}#${bindKey}"],
      [data-bind-value *= "${this.tagName.toLowerCase()}#${bindKey}"]`
    ).forEach(el => {
      if (!!el.dataset['class']) this._bindToClass(el);
      if (!!el.dataset['attribute']) this._bindToAttribute(el);
      if (!!el.dataset['bindValue']) this._bindValue(el);
    });
  }
  // _bindToVar(el, bindKey) {
  //   const boundKey = el.dataset['bind'];
  //   let bindTo = el.dataset['bindOn']; // for example, data-bind-on="value" for inputs
  //   // console.log(boundKey, bindTo, el)
  //   if (!bindTo) bindTo = this._defaultBindTypes(el.tagName);
  //   const bindKeyParts = bindKey.split('.');
  //   const storeValue = bindKeyParts.reduce((acc, key) => acc[key], this);
  //   if (!storeValue) return;
  //   el[bindTo] = storeValue;
  // }

  _bindToClass(el) {
    const bindToClass = el.dataset['class']; // data-class="{active: this.data.isActive}"
    if (bindToClass) {
      // since will always be wrapped in curly braces, remove them
      const classes = bindToClass.slice(1, -1).split(',');
      classes.forEach(className => {
        const [possibleClass, stringFunction] = className.split(':');
        let innerFunctionString = `(() => ${stringFunction})()`;
        innerFunctionString = innerFunctionString.replaceAll(`${this.tagName.toLowerCase()}#`, 'this.');
        const isTruthy = new Function(`return ${innerFunctionString}`).bind(this)();
        if (isTruthy) {
          el.classList.add(possibleClass.trim());
        } else {
          el.classList.remove(possibleClass.trim());
        }
      });
    }
  }
  _bindToAttribute(el) {
    const bindToAttributes = el.dataset['attribute']; // data-attribute="{active: this.data.isActive}"
    if (bindToAttributes) {
      // since will always be wrapped in curly braces, remove them
      const attributes = bindToAttributes.slice(1, -1).split(',');
      attributes.forEach(className => {
        const [possibleAttribute, stringFunction] = className.split(':');
        let innerFunctionString = `(() => ${stringFunction})()`;
        innerFunctionString = innerFunctionString.replaceAll(`${this.tagName.toLowerCase()}#`, 'this.');
        const value = new Function(`return ${innerFunctionString}`).bind(this)();
        if (value) {
          el.setAttribute(possibleAttribute.trim(), value);
          // console.log('jeshua setting attribute', possibleAttribute.trim(), value)
        } else {
          el.removeAttribute(possibleAttribute.trim());
        }
      });
    }
  }
  _bindValue(el) {
    const bindValue = el.dataset['bindValue']; // isActive === true
    if (bindValue) {
      let bindTo = el.dataset['bindOn']; // for example, data-bind-on="checked" for inputs
      if (!bindTo) bindTo = this._defaultBindTypes(el.tagName);
      // console.log(bindValue, bindTo, el)
      const innerFunctionString = bindValue;
      let stringFunction = `(() => ${innerFunctionString})()`;
      stringFunction = stringFunction.replaceAll(`${this.tagName.toLowerCase()}#`, 'this.');
      const value = new Function(`return ${stringFunction}`).bind(this)();
      el[bindTo] = value;
    }
  }

  _debounce(callback, wait) {
    clearTimeout(this._debounceTimeout);
    this._debounceTimeout = setTimeout(callback, wait);
  }

  _money(value) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
  }

}
